import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Key, AlertCircle, CheckCircle, Loader2, Gift, Sparkles, CreditCard, Car } from 'lucide-react';
import { activateCard } from '../api/user';
import { fetchUserProfile } from '../store/userSlice';

export default function CardActivation() {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const dispatch = useDispatch();
 const { user } = useSelector((state) => state.user);

 // 表单状态
 const [cardCode, setCardCode] = useState('');
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState(false);
 const [activationResult, setActivationResult] = useState(null);

 // 处理输入变化
 const handleInputChange = (e) => {
 setCardCode(e.target.value);
 setError('');
 };

 // 验证卡密格式
 const validateCardCode = () => {
 if (!cardCode.trim()) {
 setError('请输入卡密');
 return false;
 }

 return true;
 };

 // 提交激活
 const handleSubmit = async (e) => {
 e.preventDefault();
 
 if (!validateCardCode()) return;

 setLoading(true);
 setError('');

 try {
 // 获取当前用户的uid
 const uid = user?.uid || user?.email;
 
 if (!uid) {
 setError('用户信息未找到，请重新登录');
 setLoading(false);
 return;
 }

 // 调用API激活卡密
 const response = await activateCard({
 uid: uid,
 card_code: cardCode.trim() // 移除首尾空格
 });

 if (response.status === "1") {
 // 激活成功，立即刷新用户信息
 try {
 await dispatch(fetchUserProfile());
 console.log('✅ 卡密激活成功后，用户信息已刷新');
 } catch (error) {
 console.error('❌ 刷新用户信息失败:', error);
 }
 
 const mockResult = {
 productName: '订阅服务',
 duration: '已延长',
 expiryDate: '请查看个人中心',
 benefits: [
 '订阅有效期已延长',
 '享受完整服务权益',
 '感谢您的支持'
 ]
 };
 
 setActivationResult(mockResult);
 setSuccess(true);
 } else {
 // 激活失败
 setError(response.message || '卡密激活失败');
 }
 } catch (error) {
 console.error('卡密激活失败:', error);
 setError(error.response?.data?.message || '卡密激活失败，请稍后重试');
 } finally {
 setLoading(false);
 }
 };

 // 重置表单
 const handleReset = () => {
 setCardCode('');
 setError('');
 setSuccess(false);
 setActivationResult(null);
 };

 // 如果激活成功，显示成功页面
 if (success && activationResult) {
 return (
 <div className="route-surface account-form-page min-h-screen bg-gradient-to-br from-accent-50 via-white to-amber-50 py-8 px-4">
 <div className="max-w-2xl mx-auto">
 <div className="bg-white rounded-2xl shadow-xl p-8">
 {/* 成功图标 */}
 <div className="flex flex-col items-center mb-8">
 <div className="relative">
 <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
 <CheckCircle className="w-12 h-12 text-white" />
 </div>
 <div className="absolute -top-2 -right-2">
 <Sparkles className="w-8 h-8 text-yellow-500" />
 </div>
 </div>
 <h2 className="text-2xl font-bold text-ink-900 mt-4">
 {t('cardActivation.success.title')}
 </h2>
 </div>

 {/* 激活详情 */}
 <div className="bg-gradient-to-r from-accent-50 to-amber-50 rounded-xl p-6 mb-6">
 <h3 className="text-lg font-semibold text-ink-900 mb-4">
 {t('cardActivation.success.activationDetails')}
 </h3>
 <div className="space-y-3">
 <div className="flex justify-between">
 <span className="text-ink-600">{t('cardActivation.success.product')}:</span>
 <span className="font-medium text-ink-900">{activationResult.productName}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">{t('cardActivation.success.duration')}:</span>
 <span className="font-medium text-ink-900">{activationResult.duration}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-ink-600">{t('cardActivation.success.expiryDate')}:</span>
 <span className="font-medium text-ink-900">{activationResult.expiryDate}</span>
 </div>
 </div>
 </div>

 {/* 权益列表 */}
 <div className="mb-6">
 <h3 className="text-lg font-semibold text-ink-900 mb-3">
 {t('cardActivation.success.benefits')}
 </h3>
 <ul className="space-y-2">
 {activationResult.benefits.map((benefit, index) => (
 <li key={index} className="flex items-center gap-2">
 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
 <span className="text-ink-700">{benefit}</span>
 </li>
 ))}
 </ul>
 </div>

 {/* 操作按钮 */}
 <div className="flex gap-4">
 <button
 onClick={() => navigate('/ai-fleet')}
 className="flex-1 bg-accent-500 text-white py-3 rounded-lg font-semibold hover:bg-accent-600 transition-colors flex items-center justify-center gap-2"
 >
 <Car className="w-5 h-5" />
 {t('cardActivation.success.goToAIFleet')}
 </button>
 <button
 onClick={handleReset}
 className="flex-1 border border-ink-300 text-ink-700 py-3 rounded-lg font-semibold hover:bg-cream-100 transition-colors"
 >
 {t('cardActivation.success.activateAnother')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="route-surface account-form-page min-h-screen bg-gradient-to-br from-accent-50 via-white to-amber-50 py-8 px-4">
 <div className="max-w-md mx-auto">
 {/* 页面标题 */}
 <div className="text-center mb-8">
 <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-accent-400 to-accent-600 rounded-2xl mb-4 shadow-lg">
 <Key className="w-8 h-8 text-white" />
 </div>
 <h1 className="text-3xl font-bold text-ink-900 mb-2">
 {t('cardActivation.title')}
 </h1>
 <p className="text-ink-600">
 {t('cardActivation.subtitle')}
 </p>
 </div>

 {/* 主表单区域 */}
 <div className="bg-white rounded-2xl shadow-xl p-8">
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* 错误提示 */}
 {error && (
 <div className="bg-red-50 border border-red-200 rounded-lg p-4">
 <div className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
 <span className="text-red-800 text-sm">{error}</span>
 </div>
 </div>
 )}

 {/* 卡密输入区域 */}
 <div>
 <label htmlFor="cardCode" className="block text-sm font-medium text-ink-700 mb-2">
 {t('cardActivation.form.cardCode')}
 </label>
 <div className="relative">
 <div className="absolute left-3 top-1/2 -translate-y-1/2">
 <CreditCard className="w-5 h-5 text-ink-400" />
 </div>
 <input
 type="text"
 id="cardCode"
 value={cardCode}
 onChange={handleInputChange}
 className="w-full pl-12 pr-4 py-3 border border-ink-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent font-mono text-center text-lg tracking-wider"
 placeholder="输入或粘贴卡密"
 disabled={loading}
 autoComplete="off"
 spellCheck="false"
 />
 </div>
 </div>


 {/* 提交按钮 */}
 <button
 type="submit"
 className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 rounded-lg font-semibold hover:from-accent-600 hover:to-accent-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
 disabled={loading || !cardCode}
 >
 {loading ? (
 <>
 <Loader2 className="w-5 h-5 animate-spin" />
 {t('cardActivation.form.activating')}
 </>
 ) : (
 <>
 <Sparkles className="w-5 h-5" />
 {t('cardActivation.form.activate')}
 </>
 )}
 </button>
 </form>

 {/* 帮助信息 */}
 <div className="mt-6 space-y-4">
 <div className="border-t border-ink-200 pt-4">
 <h3 className="text-sm font-medium text-ink-900 mb-2">
 {t('cardActivation.help.title')}
 </h3>
 <ul className="space-y-2 text-sm text-ink-600">
 <li className="flex items-start gap-2">
 <span className="text-accent-500 mt-0.5">•</span>
 <span>卡密无过期时间，从激活之后开始计时</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="text-accent-500 mt-0.5">•</span>
 <span>多张卡密，有效时间会进行累加</span>
 </li>
 </ul>
 </div>

 {/* 联系支持 */}
 <div className="text-center text-sm text-ink-600">
 <p>{t('cardActivation.help.needHelp')}</p>
 <button
 onClick={() => navigate('/support-sidebar')}
 className="text-accent-600 hover:text-accent-700 font-medium mt-1"
 >
 {t('cardActivation.help.contactSupport')}
 </button>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}